// Pre-order Tracking JavaScript

/**
 * Fetch order status from API
 * @param {string} orderNumber - The order number to look up
 * @param {string} email - The email address to verify
 * @returns {Promise<Object>} Order status data
 */
async function fetchOrderStatus(orderNumber, email) {
  try {
    // Send email as query parameter for GET request
    const url = `https://preorder-e48o.onrender.com/api/order-status/${encodeURIComponent(orderNumber)}?email=${encodeURIComponent(email)}`;
    
    // Add timeout to prevent hanging (30 seconds)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      mode: 'cors',
      credentials: 'omit',
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      let errorMessage = 'Order not found. Please check your order number and try again.';
      try {
        const errorData = await response.json();
        if (errorData.error) {
          errorMessage = errorData.error;
        }
      } catch (e) {
        // If JSON parsing fails, use default message based on status code
        if (response.status === 401 || response.status === 403) {
          errorMessage = 'The email address does not match this order. Please check and try again.';
        } else if (response.status === 404) {
          errorMessage = 'Order not found. Please check your order number and try again.';
        } else if (response.status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        }
      }
      return {
        success: false,
        error: errorMessage,
        statusCode: response.status // Include status code for frontend to handle email errors
      };
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching order status:', error);
    
    // Handle different types of network errors
    let errorMessage = 'Unable to connect to the server. Please try again later.';
    
    if (error.name === 'AbortError') {
      // Request timed out
      errorMessage = 'The request took too long. The server may be slow or unresponsive. Please try again later.';
    } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
      // Network error or CORS issue
      errorMessage = 'Unable to connect to the server. This may be due to a network issue or browser security settings. Please check your internet connection and try again.';
    } else if (error.name === 'NetworkError' || error.message.includes('NetworkError')) {
      errorMessage = 'Network error. Please check your internet connection and try again.';
    }
    
    return {
      success: false,
      error: errorMessage
    };
  }
}

/**
 * Format date string for display
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {string} Formatted date string
 */
function formatDate(dateString) {
  if (!dateString) return 'N/A';
  
  try {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  } catch (e) {
    return dateString;
  }
}

/**
 * Format date range for display
 * @param {string} earliest - Earliest date
 * @param {string} latest - Latest date
 * @returns {string} Formatted date range
 */
function formatDateRange(earliest, latest) {
  if (!earliest || !latest) return 'N/A';
  
  const earliestFormatted = formatDate(earliest);
  const latestFormatted = formatDate(latest);
  
  if (earliestFormatted === latestFormatted) {
    return earliestFormatted;
  }
  
  return `${earliestFormatted} - ${latestFormatted}`;
}

/**
 * Get step status based on batchStatus and current date
 * @param {string} batchStatus - Current batch status
 * @param {Object} timeline - Timeline object
 * @param {number} stepIndex - The step.index value (internal step identifier)
 * @param {boolean} hasProductionStep - Whether Production step is included in timeline
 * @param {number} arrayIndex - The index of this step in the visible steps array
 * @param {number} currentActiveStepArrayIndex - The index of the current active step in the visible steps array
 * @returns {string} Status: 'completed', 'active', or 'pending'
 */
function getStepStatus(batchStatus, timeline, stepIndex, hasProductionStep = true, arrayIndex = -1, currentActiveStepArrayIndex = -1) {
  if (!timeline) return 'pending';
  
  const now = new Date();
  const stepDates = getStepDates(timeline, stepIndex, hasProductionStep);
  
  // Use array indices for comparison (visible steps array)
  if (arrayIndex >= 0 && currentActiveStepArrayIndex >= 0) {
    if (arrayIndex < currentActiveStepArrayIndex) {
      return 'completed';
    } else if (arrayIndex === currentActiveStepArrayIndex) {
      return 'active';
    } else {
      // Check if step date has passed
      if (stepDates.endDate) {
        const endDate = new Date(stepDates.endDate + 'T23:59:59');
        if (now > endDate) {
          return 'completed';
        }
      } else if (stepDates.date) {
        const stepDate = new Date(stepDates.date + 'T23:59:59');
        if (now > stepDate) {
          return 'completed';
        }
      }
      return 'pending';
    }
  }
  
  // Fallback to old logic if array indices not provided
  let currentStepIndex = -1;
  switch (batchStatus) {
    case 'being_produced':
      currentStepIndex = hasProductionStep ? 1 : 1; // In Transit when Production hidden
      break;
    case 'in_transit':
      currentStepIndex = hasProductionStep ? 2 : 1; // In Transit
      break;
    case 'at_warehouse':
      currentStepIndex = hasProductionStep ? 3 : 2; // Warehouse Processing
      break;
    case 'completed':
      currentStepIndex = hasProductionStep ? 4 : 3; // Delivery Date
      break;
  }
  
  if (stepIndex < currentStepIndex) {
    return 'completed';
  } else if (stepIndex === currentStepIndex) {
    return 'active';
  } else {
    // Check if step date has passed
    if (stepDates.endDate) {
      const endDate = new Date(stepDates.endDate + 'T23:59:59');
      if (now > endDate) {
        return 'completed';
      }
    } else if (stepDates.date) {
      const stepDate = new Date(stepDates.date + 'T23:59:59');
      if (now > stepDate) {
        return 'completed';
      }
    }
    return 'pending';
  }
}

/**
 * Get dates for a specific timeline step
 * @param {Object} timeline - Timeline object
 * @param {number} stepIndex - Index of the step (0 = Order Placed, 1 = Production or In Transit, etc.)
 * @param {boolean} hasProductionStep - Whether Production step is included in timeline
 * @returns {Object} Object with date or date range
 */
function getStepDates(timeline, stepIndex, hasProductionStep = true) {
  // Map step index to timeline data based on whether Production is shown
  if (stepIndex === 0) {
    // Order Placed - always first
    return { date: timeline.orderPlaced };
  }
  
  if (hasProductionStep) {
    // Production is shown
    switch (stepIndex) {
      case 1: // Production
        return { 
          startDate: timeline.productionStart, 
          endDate: timeline.factoryShipping 
        };
      case 2: // In Transit
        return { 
          startDate: timeline.factoryToWarehouseTransit?.start, 
          endDate: timeline.factoryToWarehouseTransit?.end 
        };
      case 3: // Warehouse Processing
        return { 
          startDate: timeline.warehouseHandling?.start, 
          endDate: timeline.warehouseHandling?.end 
        };
      case 4: // Delivery Date
        return { 
          startDate: timeline.carrierShipping?.earliest, 
          endDate: timeline.carrierShipping?.latest 
        };
      default:
        return {};
    }
  } else {
    // Production is skipped
    switch (stepIndex) {
      case 1: // In Transit (first step after Order Placed)
        return { 
          startDate: timeline.factoryToWarehouseTransit?.start, 
          endDate: timeline.factoryToWarehouseTransit?.end 
        };
      case 2: // Warehouse Processing
        return { 
          startDate: timeline.warehouseHandling?.start, 
          endDate: timeline.warehouseHandling?.end 
        };
      case 3: // Delivery Date
        return { 
          startDate: timeline.carrierShipping?.earliest, 
          endDate: timeline.carrierShipping?.latest 
        };
      default:
        return {};
    }
  }
}

/**
 * Get step label
 * @param {number} stepIndex - Index of the step (0-4 after removing steps)
 * @returns {string} Step label
 */
function getStepLabel(stepIndex) {
  const labels = [
    'Order Placed',
    'Production',
    'In Transit',
    'Warehouse Processing',
    'Delivery Date'
  ];
  return labels[stepIndex] || '';
}

/**
 * Render timeline for a line item
 * @param {HTMLElement} container - Container element to append timeline to
 * @param {Object} lineItem - Line item object from API
 * @param {string} sectionId - Section ID for unique IDs
 * @param {number} itemIndex - Index of the line item
 */
function renderTimeline(container, lineItem, sectionId, itemIndex) {
  const lineItemDiv = document.createElement('div');
  lineItemDiv.className = 'preorder-tracking__line-item';
  lineItemDiv.id = `line-item-${sectionId}-${itemIndex}`;
  
  // Line item header
  const header = document.createElement('div');
  header.className = 'preorder-tracking__line-item-header';
  
  // Product name - use productName, name, or title if available, otherwise use a generic label
  const productName = lineItem.productName || lineItem.name || lineItem.title || lineItem.product_title || null;
  const productInfo = document.createElement('div');
  productInfo.className = 'preorder-tracking__line-item-product';
  
  if (productName) {
    productInfo.innerHTML = `<strong>${productName}</strong> <span class="preorder-tracking__quantity">(Qty: ${lineItem.quantity})</span>`;
  } else {
    // Fallback if product name is not available in API
    productInfo.innerHTML = `<strong>Product</strong> <span class="preorder-tracking__quantity">(Qty: ${lineItem.quantity})</span>`;
  }
  
  header.appendChild(productInfo);
  
  // Expected dates if available
  if (lineItem.expectedAtWarehouse) {
    const expectedInfo = document.createElement('div');
    expectedInfo.className = 'preorder-tracking__line-item-expected';
    expectedInfo.innerHTML = `<strong>Expected at Warehouse:</strong> ${formatDate(lineItem.expectedAtWarehouse)}`;
    header.appendChild(expectedInfo);
  }
  
  if (lineItem.expectedDeliveryDateEarliest && lineItem.expectedDeliveryDateLatest) {
    const deliveryInfo = document.createElement('div');
    deliveryInfo.className = 'preorder-tracking__line-item-delivery';
    deliveryInfo.innerHTML = `<strong>Expected Delivery:</strong> ${formatDateRange(lineItem.expectedDeliveryDateEarliest, lineItem.expectedDeliveryDateLatest)}`;
    header.appendChild(deliveryInfo);
  }
  
  lineItemDiv.appendChild(header);
  
  // Timeline
  if (lineItem.timeline) {
    const timelineDiv = document.createElement('div');
    timelineDiv.className = 'preorder-tracking__timeline';
    timelineDiv.id = `timeline-${sectionId}-${itemIndex}`;
    
    // Timeline steps - Order Placed first, removed Factory Shipping, Warehouse Arrival, and Delivery
    // Check if order was placed after production completed (factoryShipping)
    const orderPlacedDate = new Date(lineItem.timeline.orderPlaced + 'T00:00:00');
    const factoryShippingDate = lineItem.timeline.factoryShipping ? new Date(lineItem.timeline.factoryShipping + 'T00:00:00') : null;
    const productionAlreadyDone = factoryShippingDate && orderPlacedDate >= factoryShippingDate;
    
    const steps = [];
    let stepIndex = 0;
    
    // Always start with Order Placed
    steps.push({ index: stepIndex++, label: 'Order Placed', date: lineItem.timeline.orderPlaced });
    
    // Only show Production if order was placed before production completed
    if (!productionAlreadyDone) {
      steps.push({ 
        index: stepIndex++, 
        label: 'Production', 
        startDate: lineItem.timeline.productionStart, 
        endDate: lineItem.timeline.factoryShipping 
      });
    }
    
    // Continue with remaining steps
    steps.push({ 
      index: stepIndex++, 
      label: 'In Transit', 
      startDate: lineItem.timeline.factoryToWarehouseTransit?.start, 
      endDate: lineItem.timeline.factoryToWarehouseTransit?.end, 
      duration: lineItem.timeline.factoryToWarehouseTransit?.duration 
    });
    steps.push({ 
      index: stepIndex++, 
      label: 'Warehouse Processing', 
      startDate: lineItem.timeline.warehouseHandling?.start, 
      endDate: lineItem.timeline.warehouseHandling?.end, 
      duration: lineItem.timeline.warehouseHandling?.duration 
    });
    steps.push({ 
      index: stepIndex++, 
      label: 'Delivery Date', 
      startDate: lineItem.timeline.carrierShipping?.earliest, 
      endDate: lineItem.timeline.carrierShipping?.latest, 
      duration: lineItem.timeline.carrierShipping?.duration 
    });
    
    let lastCompletedIndex = -1;
    const hasProductionStep = !productionAlreadyDone;
    
    // Determine the current active step index in the visible steps array
    let currentActiveStepArrayIndex = -1;
    switch (lineItem.batchStatus) {
      case 'being_produced':
        // If Production is shown, it's at index 1; if not, In Transit is at index 1
        currentActiveStepArrayIndex = hasProductionStep ? 1 : 1; // In Transit when Production hidden
        break;
      case 'in_transit':
        currentActiveStepArrayIndex = hasProductionStep ? 2 : 1; // In Transit
        break;
      case 'at_warehouse':
        currentActiveStepArrayIndex = hasProductionStep ? 3 : 2; // Warehouse Processing
        break;
      case 'completed':
        currentActiveStepArrayIndex = hasProductionStep ? 4 : 3; // Delivery Date
        break;
    }
    
    steps.forEach((step, index) => {
      const stepStatus = getStepStatus(lineItem.batchStatus, lineItem.timeline, step.index, hasProductionStep, index, currentActiveStepArrayIndex);
      if (stepStatus === 'completed') {
        lastCompletedIndex = index;
      }
      
      const stepElement = document.createElement('div');
      stepElement.className = `preorder-tracking__timeline-step preorder-tracking__timeline-step--${stepStatus}`;
      
      // Step indicator
      const indicator = document.createElement('div');
      indicator.className = 'preorder-tracking__timeline-indicator';
      stepElement.appendChild(indicator);
      
      // Step content
      const content = document.createElement('div');
      content.className = 'preorder-tracking__timeline-content';
      
      const label = document.createElement('div');
      label.className = 'preorder-tracking__timeline-label';
      label.textContent = step.label;
      content.appendChild(label);
      
      const dateInfo = document.createElement('div');
      dateInfo.className = 'preorder-tracking__timeline-date';
      
      if (step.date) {
        dateInfo.textContent = formatDate(step.date);
      } else if (step.startDate && step.endDate) {
        // Ensure dates are in correct order (startDate should be before endDate)
        const startDateObj = new Date(step.startDate + 'T00:00:00');
        const endDateObj = new Date(step.endDate + 'T00:00:00');
        
        // If startDate is after endDate, swap them
        if (startDateObj > endDateObj) {
          dateInfo.textContent = formatDateRange(step.endDate, step.startDate);
        } else {
          dateInfo.textContent = formatDateRange(step.startDate, step.endDate);
        }
        
        if (step.duration) {
          const durationText = document.createElement('span');
          durationText.className = 'preorder-tracking__timeline-duration';
          durationText.textContent = ` (${step.duration}${typeof step.duration === 'string' ? '' : ' business days'})`;
          dateInfo.appendChild(durationText);
        }
      } else {
        dateInfo.textContent = 'Pending';
      }
      
      content.appendChild(dateInfo);
      stepElement.appendChild(content);
      
      timelineDiv.appendChild(stepElement);
    });
    
    // Add timeline to container
    lineItemDiv.appendChild(timelineDiv);
    
    // Update completed line width based on last completed step
    // Use setTimeout to ensure DOM is rendered and we can measure
    setTimeout(() => {
      // Recalculate step statuses using the correct hasProductionStep flag
      let actualLastCompletedIndex = -1;
      let activeStepIndex = -1;
      
      // Recalculate current active step array index
      let currentActiveStepArrayIndex = -1;
      switch (lineItem.batchStatus) {
        case 'being_produced':
          currentActiveStepArrayIndex = hasProductionStep ? 1 : 1; // In Transit when Production hidden
          break;
        case 'in_transit':
          currentActiveStepArrayIndex = hasProductionStep ? 2 : 1; // In Transit
          break;
        case 'at_warehouse':
          currentActiveStepArrayIndex = hasProductionStep ? 3 : 2; // Warehouse Processing
          break;
        case 'completed':
          currentActiveStepArrayIndex = hasProductionStep ? 4 : 3; // Delivery Date
          break;
      }
      
      steps.forEach((step, index) => {
        const stepStatus = getStepStatus(lineItem.batchStatus, lineItem.timeline, step.index, hasProductionStep, index, currentActiveStepArrayIndex);
        if (stepStatus === 'completed') {
          actualLastCompletedIndex = index;
        } else if (stepStatus === 'active' && activeStepIndex === -1) {
          activeStepIndex = index;
        }
      });
      
      if (steps.length > 0) {
        if (activeStepIndex >= 0 && activeStepIndex > actualLastCompletedIndex) {
          // For active step, show partial completion (50% of the way to next step)
          const activePercent = ((activeStepIndex + 0.5) / steps.length) * 100;
          timelineDiv.style.setProperty('--completed-width', activePercent.toString());
          timelineDiv.classList.add('has-active-step');
        } else if (actualLastCompletedIndex >= 0) {
          // Show progress up to the last completed step
          const completedPercent = ((actualLastCompletedIndex + 1) / steps.length) * 100;
          timelineDiv.style.setProperty('--completed-width', completedPercent.toString());
          timelineDiv.classList.remove('has-active-step');
        } else {
          // No completed steps yet
          timelineDiv.style.setProperty('--completed-width', '0');
          timelineDiv.classList.remove('has-active-step');
        }
      } else {
        timelineDiv.style.setProperty('--completed-width', '0');
        timelineDiv.classList.remove('has-active-step');
      }
    }, 100);
  } else {
    // Item without timeline (in-stock item)
    const noTimelineMsg = document.createElement('div');
    noTimelineMsg.className = 'preorder-tracking__no-timeline';
    noTimelineMsg.textContent = 'This item is in stock and does not have a pre-order timeline.';
    lineItemDiv.appendChild(noTimelineMsg);
  }
  
  container.appendChild(lineItemDiv);
}

// Make functions globally available
window.fetchOrderStatus = fetchOrderStatus;
window.renderTimeline = renderTimeline;
